import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Mail, Phone, Calendar, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { QuizResultsPanel } from "@/components/admin/QuizResultsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fc_admin_token";

const AdminLogin = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/login`, { token: pw });
      localStorage.setItem(TOKEN_KEY, pw);
      onLogin(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-sm border border-border bg-card p-8"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Lock size={20} />
        </div>
        <h1 className="mt-6 font-display text-3xl font-medium text-primary">
          Admin sign-in
        </h1>
        <p className="mt-2 text-sm text-foreground/70">
          Enter the admin password to view your leads.
        </p>
        <div className="mt-6">
          <Input
            data-testid="admin-password"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Admin password"
            className="h-11"
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-btn"
          className="mt-4 w-full rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const LeadRow = ({ lead, index }) => {
  const date = lead.created_at ? new Date(lead.created_at).toLocaleString("en-IN") : "—";
  const isPaid = (lead.message || "").startsWith("PAID");
  return (
    <tr data-testid={`admin-lead-row-${index}`} className="border-b border-border last:border-0">
      <td className="p-4 align-top">
        <div className="flex items-center gap-2 font-semibold text-primary">
          <User size={14} className="text-accent" /> {lead.name || "—"}
        </div>
        <div className="mt-1 flex items-center gap-2 text-sm text-foreground/70">
          <Mail size={13} /> <a href={`mailto:${lead.email}`} className="hover:text-primary">{lead.email}</a>
        </div>
        {lead.phone && (
          <div className="mt-1 flex items-center gap-2 text-sm text-foreground/70">
            <Phone size={13} /> {lead.phone}
          </div>
        )}
      </td>
      <td className="p-4 align-top text-sm">
        <span className="whitespace-nowrap rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold text-primary">
          {lead.session_type}
        </span>
        {isPaid && (
          <span className="ml-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
            PAID
          </span>
        )}
      </td>
      <td className="p-4 align-top text-sm text-foreground/80 max-w-md whitespace-pre-line">
        {lead.message || "—"}
      </td>
      <td className="p-4 align-top text-xs text-foreground/60 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Calendar size={12} /> {date}
        </div>
      </td>
    </tr>
  );
};

const LeadsTable = ({ leads, loading }) => (
  <div className="overflow-x-auto rounded-sm border border-border bg-card">
    <table className="w-full min-w-[720px] text-left">
      <thead>
        <tr className="border-b border-border bg-muted/50">
          <th className="p-4 text-xs font-bold uppercase tracking-wider text-primary">Contact</th>
          <th className="p-4 text-xs font-bold uppercase tracking-wider text-primary">Interested in</th>
          <th className="p-4 text-xs font-bold uppercase tracking-wider text-primary">Message</th>
          <th className="p-4 text-xs font-bold uppercase tracking-wider text-primary">Received</th>
        </tr>
      </thead>
      <tbody>
        {loading && leads.length === 0 ? (
          <tr>
            <td colSpan={4} className="p-12 text-center text-foreground/60">
              Loading leads...
            </td>
          </tr>
        ) : leads.length === 0 ? (
          <tr>
            <td colSpan={4} className="p-12 text-center text-foreground/60" data-testid="admin-empty">
              No leads yet. When someone submits the booking form, they'll appear here.
            </td>
          </tr>
        ) : (
          leads.map((l, i) => <LeadRow key={l._id || i} lead={l} index={i} />)
        )}
      </tbody>
    </table>
  </div>
);

const TABS = [
  { key: "leads", label: "Leads & enquiries" },
  { key: "quiz", label: "Quiz results" },
];

const AdminDashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Couldn't load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    fetchLeads();
    setRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background px-6 py-10 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
              Admin · IFM FinCoach
            </p>
            <h1 className="mt-2 font-display text-4xl font-medium text-primary md:text-5xl">
              {tab === "leads" ? "Leads & enquiries" : "Money Passbook results"}
            </h1>
            <p className="mt-2 text-sm text-foreground/70">
              {tab === "leads" ? `${leads.length} total · newest first` : "Anonymous completions · newest first"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refresh}
              data-testid="admin-refresh"
              className="flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-1 border-b border-border" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`-mb-px border-b-2 px-5 py-3 text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-foreground/55 hover:text-primary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "leads" ? (
            <LeadsTable leads={leads} loading={loading} />
          ) : (
            <QuizResultsPanel token={token} onUnauthorized={onLogout} refreshKey={refreshKey} />
          )}
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} onLogout={logout} />;
};

export default AdminPage;
